import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useState } from 'react';

import { image_styles } from '@/constants/image_styles';

// Visually diverse curated set shown as tiles; every other style stays in the
// DOM as a text chip (revealed by "Show all") so internal links are preserved
// without rendering 250+ thumbnails like the old ImageStyles section did.
const FEATURED_STYLE_SLUGS = [
  'realistic-photo',
  'anime',
  'cyberpunk',
  'watercolor',
  'oil-painting',
  'pixar',
  '3d-model',
  'sticker',
  'logo',
  'movie-poster',
  'pencil-drawing',
  'portrait',
  'landscape',
  'pokemon',
  'mosaic',
  'icon',
];

const isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v)$/i.test(url);

const StylesWall = () => {
  const { t } = useLocale('ai-image-generator');
  const [showAll, setShowAll] = useState(false);

  const visibleStyles = image_styles.filter((style) => !style.isHidden);
  const featured = FEATURED_STYLE_SLUGS.map((slug) =>
    visibleStyles.find((style) => style.slug === slug),
  ).filter((style): style is NonNullable<typeof style> => Boolean(style));
  const rest = visibleStyles.filter(
    (style) => !FEATURED_STYLE_SLUGS.includes(style.slug),
  );

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('250+ styles on tap')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {t(
              'From photoreal to pixel art — say the style, or browse the full catalogue.',
            )}
          </p>
        </div>

        <div className='grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4 lg:grid-cols-8'>
          {featured.map((style) => (
            <Link
              key={style.slug}
              href={`/ai-image-generator/${style.slug}`}
              className='group relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]'
            >
              {style.images[0] &&
                (isVideoUrl(style.images[0].url) ? (
                  <video
                    src={style.images[0].url}
                    className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]'
                    muted
                    loop
                    playsInline
                    preload='none'
                  />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={style.images[0].url}
                    alt={style.name}
                    loading='lazy'
                    decoding='async'
                    className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]'
                  />
                ))}
              <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent' />
              <span className='absolute bottom-2 left-2 right-2 truncate text-sm font-bold text-white'>
                {style.name}
              </span>
            </Link>
          ))}
        </div>

        <div
          className={`mt-8 flex-wrap justify-center gap-2 ${
            showAll ? 'flex' : 'hidden'
          }`}
        >
          {rest.map((style) => (
            <Link
              key={style.slug}
              href={`/ai-image-generator/${style.slug}`}
              className='rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-sm font-medium text-slate-300 transition-colors hover:border-indigo-400/40 hover:text-white'
            >
              {style.name}
            </Link>
          ))}
        </div>

        {!showAll && (
          <div className='mt-10 text-center'>
            <button
              onClick={() => setShowAll(true)}
              className='rounded-full border border-white/15 bg-white/5 px-8 py-3 font-semibold text-white transition-all hover:border-white/40 hover:bg-white/10'
            >
              {t('Show all styles')} ({visibleStyles.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default StylesWall;
